import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import _get from 'lodash-es/get';
import { ErrorCodes, LoginResponse, VerificationModes, Message, MessageType, NotyService } from '@dn/libs/ui/dn-common';
import { LoginSuccessAction } from '../core/ngrx/action/auth.action';
import { APP_CONFIG } from '../core/tokens/app-config.token';
import { Environment } from '../../environments/environment.interface';
import { ApplicationState } from '../reducers';
import { Set2FactorAuthToken, SetPhone, SetCodeToken } from '../core/ngrx/action/two-factor-auth.action';
import { TokenExtractorService } from '../core/services/token-extractor/token-extractor.service';
import { Subscription } from 'rxjs';
import { AnalyticsService } from '../core/services/analytics/analytics.service';
import { AuthFlow } from '../core/enums/auth-flows.enum';
import { GtmActions, GtmLabels } from '../core/enums/analytics.enum';
import { TokenValidatorService } from '../core/services/token-validator/token-validator.service';
import notyMessage from '../core/constants/noty-messages';
import { isSafeRedirectUrl } from '../utils/isSafeRedirectUrl';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  email: any;
  redirectUrl: any;
  verificationType: any;
  displayVerification = false;
  deviceId: string;
  logoUrl: string;
  subs: Subscription[] = [];
  queryParams;
  verificationId: any;

  constructor(
    private readonly store: Store<ApplicationState>,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly analyticsService: AnalyticsService,
    private readonly extractorService: TokenExtractorService,
    private readonly tokenValidatorService: TokenValidatorService,
    private readonly tokenExtractorService: TokenExtractorService,
    private readonly notyService: NotyService,
    @Inject(APP_CONFIG) private readonly appConfig: Environment,
  ) {
    this.logoUrl = `${this.appConfig.images_url}/datanyze-logo.png`;
  }

  ngOnInit(): void {
    this.verificationId = this.tokenExtractorService.getCookieVerificationId();
    const sub = this.extractorService.getCookieDeviceId().subscribe((deviceId) => {
      if (deviceId) {
        this.deviceId = deviceId;
      }
    });
    this.subs.push(sub);
    this.queryParams = _get(this.activatedRoute, 'snapshot.queryParams');
    this.redirectUrl = new URLSearchParams(this.queryParams).get(this.appConfig.REDIRECT_KEY) || '';
  }

  loginClicked(method: string) {
    this.analyticsService.sendGtmLoginEvent(GtmActions.LOGIN_CLICK, GtmLabels.LOGIN_SCREEN, '');
    this.analyticsService.sendGtmLoginEvent(GtmActions.LOGIN_ATTEMPT, method, 'na');
  }

  loginResponse(loginResponse: LoginResponse): void {
    const { success } = loginResponse;
    const accessToken = _get(loginResponse, 'message.accessToken');
    const isGoogle = _get(loginResponse, 'message.isGoogle');
    const isOffice = _get(loginResponse, 'message.isOffice');
    const userDetails = this.tokenValidatorService.getTokenDecodedValues(accessToken);
    const userId = _get(userDetails, 'userId', null);
    if (success) {
      this.handleLoginSuccess(accessToken, userId, isGoogle, isOffice);
    } else {
      this.handleLoginFailure(loginResponse, userId, isGoogle, isOffice);
    }
  }

  handleLoginSuccess(accessToken, userId, isGoogle, isOffice) {
    const gtmLabel = isGoogle ? GtmLabels.GOOGLE : isOffice ? GtmLabels.OFFICE : GtmLabels.EMAIL;
    this.store.dispatch(LoginSuccessAction({ accessToken }));
    this.analyticsService.sendGtmLoginEvent(GtmActions.LOGIN_SUCCESS, gtmLabel, 'na', userId);
    if (this.redirectUrl && isSafeRedirectUrl(this.redirectUrl, this.appConfig)) {
      window.location.href = this.redirectUrl;
    } else {
      window.location.href = this.appConfig.platform_url;
    }
  }

  handleLoginFailure(loginResponse, userId, isGoogle, isOffice) {
    const twoFactorAuthToken = loginResponse.twoFactorAuthToken || _get(loginResponse, 'message.error.twoFactorAuthToken');
    const errorType = _get(loginResponse, 'errorType');
    const errorMsg = _get(loginResponse, 'message.error.message', null);
    const gtmLabel = isGoogle ? GtmLabels.GOOGLE : isOffice ? GtmLabels.OFFICE : GtmLabels.EMAIL;
    switch (errorType) {
      case ErrorCodes.NOT_TRUSTED_AND_NOT_PHONE_VERIFIED:
        this.store.dispatch(Set2FactorAuthToken({ twoFactorAuthToken }));
        this.router.navigate(['/two-factor-auth/phone'], { queryParams: this.queryParams, state: { authFlow: AuthFlow.LOGIN } });
        break;
      case ErrorCodes.NOT_EMAIL_VERIFIED:
        this.email = _get(loginResponse, 'email', '');
        this.verificationType = VerificationModes.VERIFICATION_NOT_VERIFIED;
        this.displayVerification = true;
        break;
      case ErrorCodes.PHONE_NOT_REGISTERED:
        this.store.dispatch(Set2FactorAuthToken({ twoFactorAuthToken }));
        this.router.navigate(['/two-factor-auth/phone'], { queryParams: this.queryParams, state: { authFlow: AuthFlow.LOGIN } });
        break;
      case ErrorCodes.TWO_FA_UNKNOWN_DEVICE:
        const { codeToken } = loginResponse;
        this.store.dispatch(Set2FactorAuthToken({ twoFactorAuthToken }));
        this.store.dispatch(SetCodeToken({ codeToken }));
        this.store.dispatch(SetPhone({ phone: _get(loginResponse, 'message.error.phone') }));
        this.router.navigate(['/two-factor-auth/verify'], { queryParams: this.queryParams, state: { authFlow: AuthFlow.LOGIN } });
        break;
      default:
        this.analyticsService.sendGtmLoginEvent(GtmActions.LOGIN_FAILED, gtmLabel, errorMsg, userId);
        break;
    }
  }

  goToSignUp() {
    this.router.navigate(['/signup'], {
      queryParams: {
        ...this.queryParams,
        [this.appConfig.SF_CAMP_ID]: _get(this.queryParams, `${this.appConfig.SF_CAMP_ID}`) || this.appConfig.SALESFORCE_CAMP_ID.goTo_signup,
      },
    });
  }

  goToForgotPassword() {
    this.router.navigate(['/forgot-password']);
  }

  goToLogin() {
    this.displayVerification = false;
  }

  resendEmailClick(event) {
    this.analyticsService.sendGtmSignupEvent(GtmActions.RESEND_EMAIL_VERIFICATION_CLICK, 'na', event);
  }

  resendSuccess() {
    this.notyService.postMessage(new Message(MessageType.SUCCESS, notyMessage.EMAIL_VERIFICATION_SENT_SUCCESS));
    this.analyticsService.sendGtmSignupEvent(GtmActions.RESEND_EMAIL_VERIFICATION_SUCCESS, 'na');
  }

  resendFailed() {
    this.notyService.postMessage(new Message(MessageType.ERROR, notyMessage.OOPS_SOMETHING_WENT_WRONG));
    this.analyticsService.sendGtmSignupEvent(GtmActions.RESEND_EMAIL_VERIFICATION_FAILED, 'na');
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
